import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Table, { CellProps } from 'rsuite/Table';
import Tooltip from 'rsuite/Tooltip';
import Pagination from 'rsuite/Pagination';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { CleanUser, RbacObject } from '../../../export-types/cleaned-types';
import { usePaginationQs } from '../../../utils/usePagination';
import Toggle from 'rsuite/Toggle';
import { getDeliveryDebtExportUrl, toMoney } from '../../../utils/common.utils';
import { Rbac, RbacSome, useRbac } from '../../common/Rbac';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import { ModalsService } from '../../../services/modals.service';
import { AuthService } from '../../../services/auth.service';
import { ProfileService } from '../../../services/profile.service';
import { AddUserModal } from './add-user-modal.component';
import { LinkUserModal } from './link-user-model.component';
import { UnLinkUserModal } from './unlink-user-model.component';
import { UnlinkObserverModal } from './unlink-observer-modal.component';
import { AddObserverModal } from './add-observer-modal.component';
import { SelfAddObserverModal } from './self-add-observer-modal.component';
import { SelfLinkUserModal } from './self-link-user-model.component';
import { SelfUnlinkObserverModal } from './self-unlink-observer-modal.component';
import { useUsers, useUsersCommission } from '../../../api/users';
import { UserBanModal } from './user-ban-modal';
import { AlertService } from '../../../services/alert.service';
import { ConfirmModal } from '../../common/confirm-modal';
import { api } from '../../../services/api';
import { useProfile } from '../../../api/profile';
import {
  UserSelectWithFilter,
  UserWithFilter,
} from '../../common/user-select-with-filter';
import moment from 'moment';
import { useSearchParamsData } from '../purchase-order/useSearchParamsData';
import { useSearchParamsPaginationEffect } from '../purchase-order/useSearchParamsPaginationEffect';
import { Button, DatePicker } from 'rsuite';
import { useOutletContext } from 'react-router-dom';
import { OutletContextParams } from './users-screen.component';
import { formatPhoneNumber } from './PhoneInput';
import { Controller, useForm } from 'react-hook-form';
import { InputWrapper } from '../../common/input-wrapper';
import { formatDateValue } from '../../../utils/date-value';
import { DeleteUserModal } from './DeleteUserModal';

export type UserProps = CleanUser & {
  moneyBill: { cny: number; usd: number };
  writeOffBill?: { cny: number; usd: number };

  deliveryDebt: number | null;
  commission?: number;
  promotionCommission?: {
    commission: number;
    endDate: Date;
  };
};

export const PhoneCell = ({ rowData, ...props }: CellProps<UserProps>) => {
  return (
    rowData && (
      <Table.Cell {...props}>
        {formatPhoneNumber(rowData.phone || '')}
      </Table.Cell>
    )
  );
};
export const UsersClientsTable = () => {
  const { hasPermission } = useRbac();
  const hasUserFullWritePermission = hasPermission(
    RbacObject.User,
    'write:admin',
  );
  const hasUnlinkPermission = hasPermission(RbacObject.User, 'unlink:admin');
  const hasLinkAdminPermission = hasPermission(RbacObject.User, 'link:admin');

  const defaultTake = 15;
  const { data: profile } = useProfile();

  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(defaultTake);

  useSearchParamsPaginationEffect(handleSetPage);

  const [searchParamsData, setSearchParamsFunctions] = useSearchParamsData();

  const [internalId, setInternalId] = useInputState(
    searchParamsData['internal'] || '',
  );
  const [manager, setManager] = useState<UserWithFilter | null>(null);
  const [takeUnattached, setTakeUnattached] = useState<boolean>(false);
  const [debouncedInternalId] = useDebouncedValue(internalId, 200);
  const [sort, setSort] = React.useState('createdAt');

  // Form for asOfDate parameter
  const { control, watch } = useForm<{ asOfDate: Date | null }>({
    defaultValues: { asOfDate: null },
  });
  const asOfDate = watch('asOfDate');

  const {
    selectedOrders,
    setSelectedOrders,
    isSelectAll,
    selectAll,
    isUnselectAll,
    unselectAll,
  } = useOutletContext<OutletContextParams>();

  useEffect(() => {
    if (searchParamsData['internal'] !== internalId) {
      setSearchParamsFunctions['internal'](internalId);
    }

    if (searchParamsData['manager'] !== (manager?.id || '')) {
      setSearchParamsFunctions['manager'](manager?.id || '');
    }
  }, [internalId, searchParamsData]);

  const { data, isLoading, mutate } = useUsers(
    ['client'],
    debouncedInternalId,
    !manager?.filterCaption ? manager?.id : undefined,
    sort,
    take,
    skip,
    takeUnattached,
  );

  const { data: commissions, isLoading: isCommissionLoading } =
    useUsersCommission(
      data && data.items ? data.items.map((user) => user.id) : [],
      true,
      {
        enabled: !isLoading && !!data && !!data.items,
      },
    );

  const sortHandleChange = () => {
    if (sort === 'createdAt') {
      setSort('deliveryDebt');
      return;
    }
    setSort('createdAt');
  };

  const renderMenu =
    (user: UserProps): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = async (eventKey: string | undefined) => {
        onClose();
        if (eventKey === 'edit-user') {
          return ModalsService.createModal(AddUserModal, { user, mutate });
        }
        if (eventKey === 'impersonate') {
          await AuthService.impersonate(user.id);
          await ProfileService.getCurrentUser();
          window.location.replace('/');
          return;
        }
        if (eventKey === 'link-manager') {
          return hasUserFullWritePermission
            ? ModalsService.createModal(LinkUserModal, { user, mutate })
            : ModalsService.createModal(SelfLinkUserModal, {
                user,
                mutate,
              });
        }
        if (eventKey === 'unlink-manager') {
          return ModalsService.createModal(UnLinkUserModal, { user, mutate });
        }
        if (eventKey === 'unlink-observer') {
          return hasUnlinkPermission
            ? ModalsService.createModal(UnlinkObserverModal, { user, mutate })
            : ModalsService.createModal(SelfUnlinkObserverModal, {
                user,
                mutate,
              });
        }
        if (eventKey === 'add-observer') {
          return hasLinkAdminPermission
            ? ModalsService.createModal(AddObserverModal, { user, mutate })
            : ModalsService.createModal(SelfAddObserverModal, { user, mutate });
        }
        if (eventKey === 'ban') {
          return ModalsService.createModal(UserBanModal, { user }).then(() => {
            AlertService.success();
            mutate();
          });
        }
        if (eventKey === 'unban') {
          return ModalsService.createModal(ConfirmModal, {
            title: 'Разблокировать пользователя?',
            buttonText: `Разблокировать`,
          }).then(() => {
            api.post(`/user/${user.id}/unban`).then(() => {
              AlertService.success();
              mutate();
            });
          });
        }
        if (eventKey === 'delete') {
          return ModalsService.createModal(DeleteUserModal, { user, mutate });
        }
      };

      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={'edit-user'}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            {user.role !== 'client' && (
              <Rbac object={RbacObject.User} action={'impersonate'}>
                <Dropdown.Item eventKey="impersonate">
                  <i className="bi bi-person-badge" />
                  <span className="ps-2">Залогиниться</span>
                </Dropdown.Item>
              </Rbac>
            )}
            {!user.manager && user.role === 'client' && (
              <Dropdown.Item eventKey={'link-manager'}>
                <i className="bi bi-link" />
                <span className="ps-2">Привязать менеджера</span>
              </Dropdown.Item>
            )}
            {user.role === 'client' &&
              (hasLinkAdminPermission ||
                !user.observers?.some(
                  (observer) => observer.id === profile?.id,
                )) && (
                <Rbac object={RbacObject.User} action={'link'}>
                  <Dropdown.Item eventKey={'add-observer'}>
                    <i className="bi bi-link" />
                    <span className="ps-2">Добавить наблюдателя</span>
                  </Dropdown.Item>
                </Rbac>
              )}
            {user.manager &&
              user.role === 'client' &&
              (hasUnlinkPermission || user.manager.id === profile?.id) && (
                <Dropdown.Item eventKey={'unlink-manager'}>
                  <i className="bi bi-link" />
                  <span className="ps-2">Отвязать менеджера</span>
                </Dropdown.Item>
              )}
            {!!user.observers?.length &&
              user.role === 'client' &&
              (hasUnlinkPermission ||
                user.observers.some(
                  (observer) => observer.id === profile?.id,
                )) && (
                <Rbac object={RbacObject.User} action={'unlink'}>
                  <Dropdown.Item eventKey={'unlink-observer'}>
                    <i className="bi bi-link" />
                    <span className="ps-2">Снять наблюдателя</span>
                  </Dropdown.Item>
                </Rbac>
              )}
            <Rbac object={RbacObject.User} action={'write:admin'}>
              {user.role === 'client' && !user.banType && (
                <Dropdown.Item eventKey="ban">
                  <i className="bi bi-ban" />
                  <span className="ps-2">Заблокировать</span>
                </Dropdown.Item>
              )}
              {user.role === 'client' && user.banType && (
                <Dropdown.Item eventKey="unban">
                  <i className="bi bi-ban" />
                  <span className="ps-2">Разблокировать</span>
                </Dropdown.Item>
              )}
              {user.role === 'client' && (
                <Dropdown.Item eventKey="delete">
                  <i className="bi bi-trash" />
                  <span className="ps-2">Удалить</span>
                </Dropdown.Item>
              )}
            </Rbac>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({ rowData, dataKey, ...props }: CellProps<UserProps>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <RbacSome
            checks={[
              { object: RbacObject.User, action: 'write:admin' },
              { object: RbacObject.User, action: 'write:toggles' },
            ]}
          >
            <Whisper
              placement="leftStart"
              trigger="click"
              speaker={renderMenu(rowData)}
            >
              <IconButton
                appearance="subtle"
                icon={<i className="bi bi-three-dots" />}
              />
            </Whisper>
          </RbacSome>
        </Table.Cell>
      )
    );
  };

  const CommissionCell = ({ rowData, ...props }: CellProps<UserProps>) => {
    return (
      rowData && (
        <Table.Cell {...props}>
          {rowData.commission ? rowData.commission + '%' : '-'}{' '}
          {rowData.promotionCommission && (
            <span className="color-red">
              ({rowData.promotionCommission.commission}% до{' '}
              {moment(rowData.promotionCommission.endDate).format('DD.MM.YYYY')}
              )
            </span>
          )}
        </Table.Cell>
      )
    );
  };

  const BalanceCell = ({ rowData, ...props }: CellProps<UserProps>) =>
    rowData && (
      <Table.Cell {...props}>
        {toMoney(rowData.moneyBill?.cny || 0, 'cny')} /{' '}
        {toMoney(rowData.moneyBill?.usd || 0, 'usd')}
      </Table.Cell>
    );

  const WriteOffBalanceCell = ({ rowData, ...props }: CellProps<UserProps>) =>
    rowData && (
      <Table.Cell {...props}>
        {toMoney(Math.abs(rowData.writeOffBill?.cny || 0), 'cny')} /{' '}
        {toMoney(Math.abs(rowData.writeOffBill?.usd || 0), 'usd')}
      </Table.Cell>
    );

  const DebtCell = ({ rowData, ...props }: CellProps<UserProps>) =>
    rowData && (
      <Table.Cell {...props}>
        {toMoney(rowData.deliveryDebt || 0, 'usd')}
      </Table.Cell>
    );

  const ManagerCell = ({ rowData, ...props }: CellProps<UserProps>) => {
    const manager = rowData?.manager?.name ?? '';

    return (
      <Table.Cell {...props}>
        {manager && (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>{manager}</Tooltip>}
          >
            <span className="d-block text-truncate">{manager}</span>
          </Whisper>
        )}
      </Table.Cell>
    );
  };

  const ObserversCell = ({ rowData, ...props }: CellProps<UserProps>) => {
    const observers =
      rowData?.observers
        ?.map((observer) => observer.name)
        .filter(Boolean)
        .join(', ') ?? '';

    return (
      <Table.Cell {...props}>
        {observers && (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>{observers}</Tooltip>}
          >
            <span className="d-block text-truncate">{observers}</span>
          </Whisper>
        )}
      </Table.Cell>
    );
  };

  const total = data?.total;
  const items = data?.items?.map((user) => {
    const commissionData = commissions?.list?.find((c) => c.id === user.id);

    return {
      ...user,
      commission: commissionData?.commission,
      promotionCommission: commissionData?.promotionCommissions?.[0],
    };
  });

  const handleManagerFilterSelect = (value: UserWithFilter | null) => {
    if (value?.filterCaption && value.id === 'unattached') {
      setTakeUnattached(true);
    } else {
      setTakeUnattached(false);
    }
    setManager(value);
  };

  useEffect(() => {
    if (isSelectAll && items) {
      const allIds = new Set(items.map((user) => user.id));
      setSelectedOrders(allIds);
      selectAll(false);
    }
  }, [isSelectAll, items, setSelectedOrders, selectAll]);

  useEffect(() => {
    if (isUnselectAll) {
      setSelectedOrders(new Set());
      unselectAll(false);
    }
  }, [isUnselectAll, setSelectedOrders, unselectAll]);

  useEffect(() => {
    setSelectedOrders(new Set());
    selectAll(false);
    unselectAll(false);
  }, [page]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pb-4">
        <div className="w-75 mr-auto d-flex" style={{ zIndex: 10 }}>
          <input
            className="form-control"
            style={{ width: '35%' }}
            type="text"
            value={internalId}
            placeholder="ID (несколько через запятую), телефон, имя"
            onChange={setInternalId}
          />
          <div className={'mx-1'} style={{ width: '30%' }}>
            <UserSelectWithFilter
              placeholder="Поиск по менеджеру"
              name={'manager'}
              additionalOptions={[
                {
                  id: 'all_users',
                  filterCaption: 'Все клиенты',
                },
                { id: 'unattached', filterCaption: 'Непривязанные' },
              ]}
              access={'salesManager'}
              value={manager}
              onChange={(selectedOption) =>
                handleManagerFilterSelect(selectedOption)
              }
              roles={['employee', 'admin']}
              searchParamsManagerId={searchParamsData['manager']}
              setManager={setManager}
            />
          </div>
        </div>
        <div className="d-flex">
          <Toggle
            checked={sort === 'deliveryDebt'}
            onChange={sortHandleChange}
          />
          <div className="ms-2">Долг доставка</div>
        </div>
        <Rbac object={RbacObject.Transaction} action={'read:admin'}>
          <div className="d-flex gap-2 align-items-end">
            <Controller
              name="asOfDate"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    title="Показать баланс на дату"
                    error={fieldState.error?.message}
                    className="w-auto"
                  >
                    <DatePicker
                      className={'w-100'}
                      format={'dd.MM.yyyy'}
                      isoWeek={true}
                      shouldDisableDate={(date) => {
                        // Disable future dates
                        return date > new Date();
                      }}
                      onChange={(v) => {
                        field.onChange(v);
                      }}
                      value={field.value}
                    />
                  </InputWrapper>
                );
              }}
            />
            <Button
              as="a"
              href={getDeliveryDebtExportUrl(
                asOfDate ? formatDateValue(asOfDate) : undefined,
              )}
              target="_blank"
              className="btn btn-success"
            >
              Скачать в Excel
            </Button>
          </div>
        </Rbac>
      </div>

      <Table
        data={items}
        headerHeight={57}
        autoHeight
        loading={isLoading || isCommissionLoading}
      >
        <Table.Column width={50}>
          <Table.HeaderCell>
            <Rbac object={RbacObject.User} action={'write:admin'}>
              <input
                className="form-check-input p-2 border-2 cursor-pointer"
                type="checkbox"
                checked={
                  items &&
                  selectedOrders.size === items.length &&
                  items.length > 0
                }
                onChange={() => {
                  if (selectedOrders.size === (items?.length || 0)) {
                    unselectAll((prev) => !prev);
                  } else {
                    selectAll((prev) => !prev);
                  }
                }}
              />
            </Rbac>
          </Table.HeaderCell>
          <Table.Cell>
            {(rowData: UserProps) => (
              <Rbac object={RbacObject.User} action={'write:admin'}>
                <input
                  className="form-check-input p-2 border-2 cursor-pointer"
                  type="checkbox"
                  checked={selectedOrders.has(rowData.id)}
                  onChange={() => {
                    setSelectedOrders((prev) => {
                      const newSet = new Set(prev);
                      if (newSet.has(rowData.id)) {
                        newSet.delete(rowData.id);
                      } else {
                        newSet.add(rowData.id);
                      }
                      return newSet;
                    });
                  }}
                />
              </Rbac>
            )}
          </Table.Cell>
        </Table.Column>
        <Table.Column flexGrow={0}>
          <Table.HeaderCell>ID</Table.HeaderCell>
          <Table.Cell dataKey="internalId" />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Имя</Table.HeaderCell>
          <Table.Cell dataKey="name" />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Номер телефона</Table.HeaderCell>
          <PhoneCell dataKey="phone" />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Email</Table.HeaderCell>
          <Table.Cell dataKey="email" />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Комиссия за выкуп</Table.HeaderCell>
          <CommissionCell />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Баланс аккаунта</Table.HeaderCell>
          <BalanceCell />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Оборот</Table.HeaderCell>
          <WriteOffBalanceCell />
        </Table.Column>
        <Table.Column flexGrow={1}>
          <Table.HeaderCell>Долг по доставке</Table.HeaderCell>
          <DebtCell />
        </Table.Column>
        <Table.Column width={95}>
          <Table.HeaderCell>Менеджер</Table.HeaderCell>
          <ManagerCell />
        </Table.Column>
        <Table.Column width={125}>
          <Table.HeaderCell style={{ paddingLeft: 4, paddingRight: 4 }}>
            <FormattedMessage
              defaultMessage="Наблюдатели"
              id="users.clients.table.observers"
              description="Заголовок колонки наблюдателей"
            />
          </Table.HeaderCell>
          <ObserversCell />
        </Table.Column>
        <Table.Column>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>

      {total && total > take && (
        <div className="mt-4 pb-4">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="md"
            layout={['pager']}
            total={total}
            limitOptions={[15, 20]}
            limit={take}
            activePage={page}
            onChangePage={handleSetPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      )}
    </>
  );
};
